import React, { useState, useMemo, useCallback } from 'react';
import {
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  RefreshControl,
  Alert,
} from 'react-native';
import { Text, useThemeName } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { Calendar } from 'lucide-react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';

// Types
import type {
  RootStackParamList,
  RootStackScreenProps,
} from '@/types/navigation';

// Components
import { Screen, ScreenHeader } from '@/components';
import { ApplicationCard } from './ApplicationCard';
import { ReminderModal } from './ReminderModal';

// API
import {
  useMyApplications,
  useUpdateApplicationStatus,
} from '@/api/hooks/useMeetingApplications';
import type { MeetingAnnouncementApplicationView } from '@/api/types';

// Utils
import { COLORS } from '@/constants/colors';
import { getStatusFromCode, ApplicationStatus } from './utils';

type Props = RootStackScreenProps<'MyApplications'>;

type FilterTab = 'all' | 'sent' | 'accepted' | 'rejected';

const MyApplicationsScreen: React.FC<Props> = ({
  navigation: rootNavigation,
}) => {
  const themeName = useThemeName();
  const { t } = useTranslation();
  const isDark = themeName === 'dark';
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const bgColor = isDark ? COLORS.BLACK : '#f5f5f5';
  const cardBg = isDark ? '#1a1a1a' : COLORS.WHITE;
  const textColor = isDark ? COLORS.WHITE : COLORS.DARK_TEXT;
  const subtitleColor = isDark ? COLORS.GRAY_400 : COLORS.GRAY_500;
  const borderColor = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)';

  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const [reminderModalVisible, setReminderModalVisible] = useState(false);
  const [selectedApplication, setSelectedApplication] =
    useState<MeetingAnnouncementApplicationView | null>(null);

  // Fetch user's applications from API
  const {
    data: applications = [],
    isLoading,
    isError,
    refetch,
    isRefetching,
  } = useMyApplications();

  // Cancel application mutation
  const { mutate: updateStatus } = useUpdateApplicationStatus({
    onSuccess: () => {
      Alert.alert(
        t('screens.myApplications.cancelledTitle', 'Anulowano'),
        t(
          'screens.myApplications.cancelledMessage',
          'Aplikacja została anulowana',
        ),
      );
    },
    onError: error => {
      Alert.alert(t('common.error', 'Błąd'), error.message);
    },
  });

  // Count applications by status
  const counts = useMemo(() => {
    const sent = applications.filter(
      a => getStatusFromCode(a.statusCode) === 'pending',
    ).length;
    const accepted = applications.filter(
      a => getStatusFromCode(a.statusCode) === 'accepted',
    ).length;
    const rejected = applications.filter(
      a => getStatusFromCode(a.statusCode) === 'rejected',
    ).length;
    return { sent, accepted, rejected, all: applications.length };
  }, [applications]);

  // Filter applications based on active filter
  const filteredApplications = useMemo(() => {
    if (activeFilter === 'all') return applications;

    const statusMap: Record<FilterTab, ApplicationStatus | null> = {
      all: null,
      sent: 'pending',
      accepted: 'accepted',
      rejected: 'rejected',
    };

    const targetStatus = statusMap[activeFilter];
    return applications.filter(
      a => getStatusFromCode(a.statusCode) === targetStatus,
    );
  }, [activeFilter, applications]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleReminderPress = useCallback(
    (application: MeetingAnnouncementApplicationView) => {
      setSelectedApplication(application);
      setReminderModalVisible(true);
    },
    [],
  );

  const handleWritePress = useCallback(() => {
    navigation.navigate('TabNavigator', { screen: 'Chat' } as any);
  }, [navigation]);

  const handleCancelPress = useCallback(
    (application: MeetingAnnouncementApplicationView) => {
      Alert.alert(
        t('screens.myApplications.cancelConfirmTitle', 'Anulować aplikację?'),
        t(
          'screens.myApplications.cancelConfirmMessage',
          'Czy na pewno chcesz anulować tę aplikację?',
        ),
        [
          { text: t('common.cancel', 'Anuluj'), style: 'cancel' },
          {
            text: t('screens.myApplications.confirmCancel', 'Tak, anuluj'),
            style: 'destructive',
            onPress: () => {
              updateStatus({
                applicationId: application.id,
                data: { statusCode: 'cancelled' },
              });
            },
          },
        ],
      );
    },
    [t, updateStatus],
  );

  const handleCalendarPress = useCallback(() => {
    navigation.navigate('MyApplicationsCalendar');
  }, [navigation]);

  const handleSaveReminder = useCallback(
    (reminders: string[]) => {
      console.log(
        'Saving reminders:',
        reminders,
        'for application:',
        selectedApplication?.id,
      );
      Alert.alert(
        t('screens.myApplications.reminderSaved', 'Zapisano'),
        t(
          'screens.myApplications.reminderSavedMessage',
          'Przypomnienia zostały ustawione',
        ),
      );
    },
    [selectedApplication, t],
  );

  const renderFilterTab = (filter: FilterTab, label: string, count: number) => {
    const isActive = activeFilter === filter;
    return (
      <TouchableOpacity
        key={filter}
        onPress={() => setActiveFilter(filter)}
        style={{ alignItems: 'center' }}
      >
        <Text
          fontSize={24}
          fontWeight="700"
          fontFamily="Poppins"
          color={isActive ? textColor : subtitleColor}
        >
          {count}
        </Text>
        <Text
          fontSize={12}
          fontFamily="Poppins"
          color={isActive ? textColor : subtitleColor}
        >
          {label}
        </Text>
        {isActive && (
          <YStack
            width={40}
            height={3}
            backgroundColor={COLORS.PRIMARY}
            borderRadius={2}
            marginTop="$1"
          />
        )}
      </TouchableOpacity>
    );
  };

  return (
    <Screen edges={['top']}>
      <YStack flex={1} backgroundColor={bgColor}>
        {/* Header */}
        <ScreenHeader
          title={t('screens.myApplications.title', 'Moje aplikacje')}
          onBackPress={() => rootNavigation.goBack()}
          rightButton={
            <TouchableOpacity onPress={handleCalendarPress}>
              <YStack
                width={40}
                height={40}
                borderRadius={20}
                backgroundColor={
                  isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
                }
                alignItems="center"
                justifyContent="center"
              >
                <Calendar
                  size={20}
                  color={isDark ? COLORS.WHITE : COLORS.DARK_TEXT}
                />
              </YStack>
            </TouchableOpacity>
          }
        />

        <ScrollView
          contentContainerStyle={{ padding: 16, paddingBottom: 100 }}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={isRefetching}
              onRefresh={handleRefresh}
              tintColor={COLORS.PRIMARY}
            />
          }
        >
          {/* Stats / Filter Tabs Card */}
          <YStack
            backgroundColor={cardBg}
            borderRadius={16}
            padding="$4"
            borderWidth={1}
            borderColor={borderColor}
            marginBottom="$4"
          >
            <XStack justifyContent="space-around">
              {renderFilterTab(
                'sent',
                t('screens.myApplications.sent', 'Wysłane'),
                counts.sent,
              )}
              {renderFilterTab(
                'accepted',
                t('screens.myApplications.accepted', 'Zaakceptowane'),
                counts.accepted,
              )}
              {renderFilterTab(
                'rejected',
                t('screens.myApplications.rejected', 'Odrzucone'),
                counts.rejected,
              )}
            </XStack>
          </YStack>

          {/* Applications List */}
          {isLoading ? (
            <YStack
              flex={1}
              alignItems="center"
              justifyContent="center"
              paddingVertical="$8"
            >
              <ActivityIndicator size="large" color={COLORS.PRIMARY} />
              <Text fontSize={14} color={subtitleColor} marginTop="$3">
                {t('screens.myApplications.loading', 'Ładowanie aplikacji...')}
              </Text>
            </YStack>
          ) : isError ? (
            <YStack
              flex={1}
              alignItems="center"
              justifyContent="center"
              paddingVertical="$8"
            >
              <Text fontSize={16} color={textColor}>
                {t(
                  'screens.myApplications.error',
                  'Nie udało się pobrać aplikacji',
                )}
              </Text>
              <TouchableOpacity onPress={handleRefresh}>
                <Text fontSize={14} color={COLORS.PRIMARY} marginTop="$2">
                  {t('screens.myApplications.retry', 'Spróbuj ponownie')}
                </Text>
              </TouchableOpacity>
            </YStack>
          ) : filteredApplications.length === 0 ? (
            <YStack
              flex={1}
              alignItems="center"
              justifyContent="center"
              paddingVertical="$8"
            >
              <Text fontSize={16} color={subtitleColor}>
                {activeFilter === 'all'
                  ? t(
                      'screens.myApplications.noApplications',
                      'Nie masz jeszcze żadnych aplikacji',
                    )
                  : activeFilter === 'sent'
                  ? t(
                      'screens.myApplications.noSent',
                      'Brak wysłanych aplikacji',
                    )
                  : activeFilter === 'accepted'
                  ? t(
                      'screens.myApplications.noAccepted',
                      'Brak zaakceptowanych aplikacji',
                    )
                  : t(
                      'screens.myApplications.noRejected',
                      'Brak odrzuconych aplikacji',
                    )}
              </Text>
            </YStack>
          ) : (
            <YStack gap="$3">
              {filteredApplications.map(application => (
                <ApplicationCard
                  key={application.id}
                  application={application}
                  cardBg={cardBg}
                  textColor={textColor}
                  subtitleColor={subtitleColor}
                  borderColor={borderColor}
                  onReminderPress={() => handleReminderPress(application)}
                  onWritePress={handleWritePress}
                  onCancelPress={() => handleCancelPress(application)}
                />
              ))}
            </YStack>
          )}
        </ScrollView>

        {/* Reminder Modal */}
        <ReminderModal
          visible={reminderModalVisible}
          onClose={() => setReminderModalVisible(false)}
          onSave={handleSaveReminder}
          meetingTitle={selectedApplication?.motivation}
        />
      </YStack>
    </Screen>
  );
};

export default MyApplicationsScreen;
